import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Checkbox,
  Grid,
  LinearProgress,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
  Card,
} from "@mui/material";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import { toastMessage } from "utils/toastMessage";
import api from "utils/apiClient";
import * as XLSX from "xlsx";
import { PageHeader, SectionCard } from "_shared/ui";

// s3://bucket/key -> https://bucket.s3.amazonaws.com/key so an <img> can load it.
const httpsUrlForS3Uri = (uri) => {
  const match = /^s3:\/\/([^/]+)\/(.+)$/i.exec(uri || "");
  if (!match) return null;
  const [, bucket, key] = match;
  // Encode each path segment but keep the "/" separators intact.
  const encodedKey = key.split("/").map(encodeURIComponent).join("/");
  return `https://${bucket}.s3.amazonaws.com/${encodedKey}`;
};

// A result/input reference is either a full https URL (load directly) or an
// s3:// URI (convert). Returns a URL an <img> can load, or null.
const previewUrlFor = (ref) => {
  const raw = `${ref ?? ""}`.trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  return httpsUrlForS3Uri(raw);
};

// Accepted input: a full https S3 URL (the backend converts it to s3://) or a
// raw s3://bucket/key. Used to count valid lines and pick refs out of a sheet.
const IMAGE_REF_REGEX = /^(https?:\/\/\S+amazonaws\.com\/\S+|s3:\/\/[^/\s]+\/.+)$/i;

// Example URLs shown in the downloadable templates and placeholder so anyone can
// see the exact shape the inspector expects and edit from there.
const SAMPLE_URIS = [
  "https://jkmsfa.s3.amazonaws.com/public/brand-shop/brand_wall/1787548019089-0.160379624920453.jpg",
  "https://jkmsfa.s3.amazonaws.com/public/brand-shop/main_signage/front.jpg",
  "https://jkmsfa.s3.amazonaws.com/public/dealer-456/main_signage/front.jpg",
];

const decisionColor = (decision) => {
  if (decision === "PASS") return "success";
  if (decision === "FAIL") return "error";
  if (decision === "ERROR") return "warning";
  return "default";
};

const modalBoxStyle = {
  width: "60%",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: "2rem",
  borderRadius: "8px",
  maxHeight: "80vh",
  overflowY: "auto",
};

// 80x80 preview slot — shows the S3 image when it loads, or a clear "no preview"
// box when the URI is invalid / the object isn't readable with these AWS creds.
// Neutral border so it reads correctly in both light and dark themes.
const PREVIEW_BOX = {
  width: 80,
  height: 80,
  borderRadius: 6,
  border: "1px solid rgba(128,128,128,0.35)",
  flexShrink: 0,
};

const ImagePreview = ({ uri }) => {
  const [failed, setFailed] = useState(false);
  const url = useMemo(() => previewUrlFor(uri), [uri]);

  if (!url || failed) {
    return (
      <Box
        sx={{
          ...PREVIEW_BOX,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "action.hover",
          color: "text.disabled",
          fontSize: 10,
          textAlign: "center",
          p: 0.25,
        }}
      >
        no preview
      </Box>
    );
  }
  return (
    <Tooltip
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: "none",
            p: 0.5,
            bgcolor: "background.paper",
            boxShadow: 4,
            border: "1px solid",
            borderColor: "divider",
          },
        },
      }}
      title={
        <img
          src={url}
          alt="preview large"
          style={{ display: "block", maxWidth: 360, maxHeight: 360, objectFit: "contain", borderRadius: 4 }}
        />
      }
    >
      <a href={url} target="_blank" rel="noreferrer">
        <img
          src={url}
          alt="preview"
          style={{ ...PREVIEW_BOX, objectFit: "cover", cursor: "zoom-in" }}
          onError={() => setFailed(true)}
        />
      </a>
    </Tooltip>
  );
};

const StatCard = ({ label, value, color }) => (
  <Card
    sx={{
      flex: "1 1 120px",
      minWidth: 110,
      p: 1.5,
      textAlign: "center",
    }}
  >
    <Typography sx={{ fontSize: 26, fontWeight: 700, color: color || "text.primary" }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: 12,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
  </Card>
);

const Main = () => {
  const [pasted, setPasted] = useState("");
  const [fileUris, setFileUris] = useState([]);
  const [fileName, setFileName] = useState("");
  const [returnV1, setReturnV1] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);
  const [rawRow, setRawRow] = useState(null);

  // Combine pasted lines + parsed file rows, validate and de-dupe.
  const { valid, invalidCount } = useMemo(() => {
    const raw = [
      ...pasted.split("\n").map((s) => s.trim()),
      ...fileUris,
    ].filter(Boolean);
    const seen = new Set();
    const validList = [];
    let invalid = 0;
    raw.forEach((uri) => {
      if (!IMAGE_REF_REGEX.test(uri)) {
        invalid += 1;
        return;
      }
      if (seen.has(uri)) return;
      seen.add(uri);
      validList.push(uri);
    });
    return { valid: validList, invalidCount: invalid };
  }, [pasted, fileUris]);

  const readUploadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const workbook = XLSX.read(ev.target.result, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false });
        const uris = rows
          .flat()
          .map((cell) => `${cell ?? ""}`.trim())
          .filter((cell) => IMAGE_REF_REGEX.test(cell));
        setFileUris(uris);
        if (uris.length === 0) toastMessage("No S3 URIs found in that file.", "error");
      } catch (err) {
        toastMessage("Could not read that file. Use a .csv or .xlsx.", "error");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleClear = () => {
    setPasted("");
    setFileUris([]);
    setFileName("");
    setSummary(null);
    setResults([]);
  };

  const handleInspect = async () => {
    if (valid.length === 0) {
      toastMessage("Add at least one valid s3://bucket/key URI.", "error");
      return;
    }
    setLoading(true);
    setSummary(null);
    setResults([]);
    try {
      const body = await api.post("/api/v2/compliance/inspect", {
        s3_uris: valid,
        return_v1_format: returnV1,
      });
      const payload = body?.data || {};
      setSummary(payload.summary || null);
      setResults(payload.results || []);
      toastMessage(body?.message || "Inspection completed.", "success");
    } catch (err) {
      // api wrapper already surfaced an error toast.
    } finally {
      setLoading(false);
    }
  };

  // Downloadable templates — generated in-browser so they always match the
  // format the endpoint expects (no hosted file to keep in sync).
  const downloadSampleExcel = () => {
    const ws = XLSX.utils.aoa_to_sheet([["image_url"], ...SAMPLE_URIS.map((u) => [u])]);
    ws["!cols"] = [{ wch: 90 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "S3 URIs");
    XLSX.writeFile(wb, "compliance-inspect-sample.xlsx");
  };

  const downloadSampleJson = () => {
    const sample = { s3_uris: SAMPLE_URIS, return_v1_format: false };
    const blob = new Blob([JSON.stringify(sample, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "compliance-inspect-sample.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCsv = () => {
    const flat = results.map((r, i) => ({
      "#": i + 1,
      image_url: r.input || r.s3_uri,
      s3_uri: r.s3_uri,
      decision: r.ok ? r.result?.decision ?? "UNKNOWN" : "ERROR",
      confidence: r.ok && typeof r.result?.confidence === "number" ? r.result.confidence : "",
      category: r.ok ? r.result?.category ?? r.result?.extracted_category ?? "" : "",
      reason: r.ok ? r.result?.reason ?? "" : typeof r.error === "string" ? r.error : JSON.stringify(r.error),
      processing_time_ms: r.ok ? r.result?.processing_time_ms ?? "" : "",
    }));
    const ws = XLSX.utils.json_to_sheet(flat);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Compliance");
    XLSX.writeFile(wb, "compliance-inspection.xlsx");
  };

  const columns = ["#", "Image / S3 URI", "Decision", "Confidence", "Category", "Reason / Error", "Time", "Raw"];

  return (
    <>
      <PageHeader
        title="Bulk Compliance Inspector"
        subtitle="Inspect S3 image URLs for brand compliance in bulk"
        action={
          <>
            <Button variant="outlined" size="small" onClick={downloadSampleExcel}>
              Sample Excel
            </Button>
            <Button variant="outlined" size="small" onClick={downloadSampleJson}>
              Sample JSON
            </Button>
          </>
        }
      />

      {/* Input panel */}
      <SectionCard sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          New here? Download the template (Sample Excel / JSON) to see the expected
          format.
        </Typography>
        <Box>
          <TextField
            label="Paste image URLs — one per line"
            placeholder={"https://jkmsfa.s3.amazonaws.com/public/brand-shop/brand_wall/a.jpg\nhttps://jkmsfa.s3.amazonaws.com/public/brand-shop/main_signage/b.jpg"}
            multiline
            minRows={6}
            fullWidth
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
          />

          <Grid container alignItems="center" spacing={2} sx={{ mt: 0.5 }}>
            <Grid item>
              <Button variant="outlined" component="label">
                {fileName ? `File: ${fileName}` : "Upload CSV / XLSX"}
                <input
                  hidden
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={readUploadFile}
                />
              </Button>
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={returnV1} onChange={(e) => setReturnV1(e.target.checked)} />}
                label="return_v1_format"
              />
            </Grid>
            <Grid item xs />
            <Grid item>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {valid.length} valid URI{valid.length === 1 ? "" : "s"}
                {invalidCount > 0 ? ` · ${invalidCount} skipped` : ""}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", gap: 2, mt: 1.5, alignItems: "center" }}>
            <Button variant="contained" onClick={handleInspect} disabled={loading || valid.length === 0}>
              {loading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Inspect"}
            </Button>
            <Button variant="text" color="error" onClick={handleClear} disabled={loading}>
              Clear
            </Button>
          </Box>
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      </SectionCard>

      {/* Summary */}
      {summary && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
            <StatCard label="Total" value={summary.total} />
            <StatCard label="Inspected" value={summary.succeeded} />
            <StatCard label="Pass" value={summary.decisions?.PASS || 0} color="success.main" />
            <StatCard label="Fail" value={summary.decisions?.FAIL || 0} color="error.main" />
            <StatCard label="Errored" value={summary.failed} color={summary.failed ? "warning.main" : "text.primary"} />
            <StatCard label="Duration" value={`${(summary.durationMs / 1000).toFixed(1)}s`} />
          </Box>
        )}

        {/* Results */}
        {(results.length > 0 || summary) && (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="outlined" onClick={downloadCsv} disabled={results.length === 0}>
                Download results
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: "60vh", overflowY: "auto", mt: 1 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((c, i) => (
                      <TableCell key={i} align={i === 1 ? "left" : "center"} sx={{ fontWeight: "bolder" }}>
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.length > 0 ? (
                    results.map((r, idx) => {
                      const res = r.result || {};
                      const decision = r.ok ? res.decision || "UNKNOWN" : "ERROR";
                      const conf =
                        r.ok && typeof res.confidence === "number"
                          ? `${Math.round(res.confidence * 100)}%`
                          : "-";
                      const time =
                        r.ok && typeof res.processing_time_ms === "number"
                          ? `${(res.processing_time_ms / 1000).toFixed(1)}s`
                          : "-";
                      const reason = r.ok
                        ? res.reason || "-"
                        : `${typeof r.error === "string" ? r.error : JSON.stringify(r.error)}${
                            r.status ? ` (HTTP ${r.status})` : ""
                          }`;
                      return (
                        <TableRow key={idx}>
                          <TableCell align="center">{idx + 1}</TableCell>
                          <TableCell align="left" sx={{ maxWidth: 360 }}>
                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                              <ImagePreview uri={r.input || r.s3_uri} />
                              <span style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>
                                {r.input || r.s3_uri}
                              </span>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={decision} color={decisionColor(decision)} size="small" />
                          </TableCell>
                          <TableCell align="center">{conf}</TableCell>
                          <TableCell align="center">{r.ok ? res.category || res.extracted_category || "-" : "-"}</TableCell>
                          <TableCell align="center" sx={{ maxWidth: 280, fontSize: 13 }}>{reason}</TableCell>
                          <TableCell align="center">{time}</TableCell>
                          <TableCell align="center">
                            <Button size="small" onClick={() => setRawRow(r)}>View</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        <Typography variant="h6" sx={{ p: 1 }}>No Data.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

      {/* Raw JSON modal */}
      <Modal open={!!rawRow} onClose={() => setRawRow(null)}>
        <Box sx={modalBoxStyle}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Raw response</Typography>
          <Typography sx={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all", mb: 1 }}>
            {rawRow?.s3_uri}
          </Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: "action.hover",
              color: "text.primary",
              p: 1.5,
              borderRadius: "6px",
              overflowX: "auto",
              fontSize: 12,
              m: 0,
            }}
          >
            {JSON.stringify(rawRow?.ok ? rawRow?.result : { error: rawRow?.error, status: rawRow?.status }, null, 2)}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button variant="contained" onClick={() => setRawRow(null)}>Close</Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

const BulkComplianceInspector = () => {
  return (
    <AccessController roles={["ADMIN"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default BulkComplianceInspector;
